import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { storiesAPI, voiceAPI, familyAPI } from '../services/api';
import HLSVideoPlayer from '../components/HLSVideoPlayer';
import './StoryDetail.css';

const REACTION_EMOJIS = ['❤️', '😂', '😢', '🙏', '💯', '🥰', '😮', '👏'];

const StoryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const photoInputRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const commentInputRef = useRef(null);
  
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeVersion, setActiveVersion] = useState('raw');
  const [audioUrl, setAudioUrl] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  
  // Voice synthesis state
  const [voiceProfiles, setVoiceProfiles] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [synthesizing, setSynthesizing] = useState(false);
  const [synthesizedUrl, setSynthesizedUrl] = useState(null);
  const [loadingSynthesized, setLoadingSynthesized] = useState(false);
  const [showVoiceModal, setShowVoiceModal] = useState(false);

  // Photo state
  const [photos, setPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [editingCaption, setEditingCaption] = useState(false);
  const [captionText, setCaptionText] = useState('');
  const [savingCaption, setSavingCaption] = useState(false);

  // Delete confirmation modals
  const [showDeleteStoryModal, setShowDeleteStoryModal] = useState(false);
  const [showDeletePhotoModal, setShowDeletePhotoModal] = useState(false);
  const [deletingStory, setDeletingStory] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState(false);

  // Attachment state
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);

  // Time capsule state
  const [showTimeCapsuleModal, setShowTimeCapsuleModal] = useState(false);
  const [timeCapsuleEnabled, setTimeCapsuleEnabled] = useState(false);
  const [unlockDate, setUnlockDate] = useState('');
  const [timeCapsuleMessage, setTimeCapsuleMessage] = useState('');
  const [savingTimeCapsule, setSavingTimeCapsule] = useState(false);

  // === NEW: Comments state ===
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [showDeleteCommentModal, setShowDeleteCommentModal] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [deletingComment, setDeletingComment] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(null);

  // Clone voice from recording
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneVoiceName, setCloneVoiceName] = useState('');
  const [cloningVoice, setCloningVoice] = useState(false);
  const [showRecloneConfirm, setShowRecloneConfirm] = useState(false);

  // Title editing
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleText, setTitleText] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);

  // Category & Tags editing
  const [editingCategory, setEditingCategory] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [customCategory, setCustomCategory] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  const [editingTags, setEditingTags] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [editableTags, setEditableTags] = useState([]);
  const [savingTags, setSavingTags] = useState(false);

  const PRESET_CATEGORIES = [
    'Early Life', 'Childhood', 'School', 'College', 'Career', 
    'Marriage', 'Family', 'Parenting', 'Military', 'Travel',
    'Immigration', 'Traditions', 'Holidays', 'Faith', 'Health',
    'Hobbies', 'Friendship', 'Loss', 'Achievement', 'Advice', 'Other'
  ];

  // === Story Linking state ===
  const [linkedStories, setLinkedStories] = useState([]);
  const [loadingLinked, setLoadingLinked] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkSearchQuery, setLinkSearchQuery] = useState('');
  const [linkSearchResults, setLinkSearchResults] = useState([]);
  const [searchingStories, setSearchingStories] = useState(false);
  const [selectedLinkRelationship, setSelectedLinkRelationship] = useState('related');
  const [linkNote, setLinkNote] = useState('');
  const [linkingStory, setLinkingStory] = useState(false);
  const [circleMembers, setCircleMembers] = useState([]);
  const [treeOnlyMembers, setTreeOnlyMembers] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [linkFilterStoryteller, setLinkFilterStoryteller] = useState('');

  // Re-enhance confirmation state
  const [showReenhanceModal, setShowReenhanceModal] = useState(false);

  // Reassign story state
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignTargetId, setReassignTargetId] = useState('');
  const [reassigning, setReassigning] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  // Permission helpers based on circle role
  const canEdit = userRole && ['storyteller', 'vaultKeeper'].includes(userRole);
  const canContribute = userRole && ['storyteller', 'vaultKeeper', 'contributor'].includes(userRole);
  const isVaultKeeper = userRole === 'vaultKeeper';

  useEffect(() => {
    loadStory();
  }, [id]);

  useEffect(() => {
    if (story?.synthesizedAudio?.[activeVersion]?.key) {
      loadSynthesizedAudio();
    } else {
      setSynthesizedUrl(null);
    }
  }, [activeVersion, story]);

  const loadStory = async () => {
    try {
      const res = await storiesAPI.getOne(id);
      setStory(res.data.story);

      // If story is mid-enhancement, disable button and poll until done
      if (res.data.story.status === 'enhancing' && !res.data.story.enhancedVersions?.polished) {
        setEnhancing(true);
        pollEnhancementStatus(res.data.story._id);
      }
      
      if (res.data.story.enhancedVersions?.polished) {
        setActiveVersion('polished');
      }

      // Load voice profiles
      if (res.data.story.familyCircleId) {
        const familyId = res.data.story.familyCircleId._id || res.data.story.familyCircleId;
        try {
          const voiceRes = await voiceAPI.getProfiles(familyId);
          setVoiceProfiles(voiceRes.data.voiceProfiles || []);
          if (voiceRes.data.voiceProfiles?.length > 0) {
            setSelectedVoice(voiceRes.data.voiceProfiles[0]._id);
          }
        } catch (err) {
          console.log('No voice profiles yet');
        }

        // Load circle members for storyteller filter
        try {
          const memberRes = await familyAPI.getMembers(familyId);
          setCircleMembers(memberRes.data.members || []);
          // Determine current user's role in this circle
          const me = (memberRes.data.members || []).find(m => 
            (m.userId?._id || m.userId) === currentUser._id
          );
          setUserRole(me?.role || null);
          // Load tree-only members (no account) for reassignment
          try {
            const treeRes = await storiesAPI.getTreeOnlyMembers(familyId);
            setTreeOnlyMembers(treeRes.data.treeOnlyMembers || []);
          } catch (treeErr) {
            console.log('Could not load tree-only members');
          }
        } catch (err) {
          console.log('Could not load members');
        }
      }

      // Load photos
      if (res.data.story.photos?.length > 0) {
        loadPhotos();
      }

      // Load attachments
      if (res.data.story.attachments?.length > 0) {
        loadAttachments();
      }

      // Load comments
      loadComments();

      // Load linked stories
      loadLinkedStories();

      // Initialize time capsule state
      if (res.data.story.timeCapsule) {
        setTimeCapsuleEnabled(res.data.story.timeCapsule.enabled || false);
        if (res.data.story.timeCapsule.unlockDate) {
          const date = new Date(res.data.story.timeCapsule.unlockDate);
          setUnlockDate(date.toISOString().split('T')[0]);
        }
        setTimeCapsuleMessage(res.data.story.timeCapsule.message || '');
      }
    } catch (error) {
      console.error('Error loading story:', error);
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const loadPhotos = async () => {
    setLoadingPhotos(true);
    try {
      const res = await storiesAPI.getPhotos(id);
      setPhotos(res.data.photos || []);
    } catch (error) {
      console.error('Error loading photos:', error);
    } finally {
      setLoadingPhotos(false);
    }
  };

  const loadAttachments = async () => {
    try {
      const res = await storiesAPI.getAttachments(id);
      setAttachments(res.data.attachments || []);
    } catch (error) {
      console.error('Error loading attachments:', error);
    }
  };

  const handleAttachmentUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadingAttachments(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      await storiesAPI.uploadAttachments(id, formData);
      await loadAttachments();
    } catch (error) {
      console.error('Attachment upload error:', error);
      if (error.response?.status === 403) {
        alert('Only Storytellers and Vault Keepers can upload files. Please ask a Vault Keeper to upgrade your role.');
      } else {
        alert(error.response?.data?.error || 'Failed to upload files');
      }
    } finally {
      setUploadingAttachments(false);
      e.target.value = '';
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await storiesAPI.deleteAttachment(id, attachmentId);
      setAttachments(attachments.filter(a => a._id !== attachmentId));
    } catch (error) {
      console.error('Delete attachment error:', error);
      if (error.response?.status === 403) {
        alert('Only Storytellers and Vault Keepers can delete attachments. Please ask a Vault Keeper to upgrade your role.');
      } else {
        alert('Failed to delete attachment');
      }
    }
  };

  const getFileIcon = (fileType) => {
    const icons = {
      pdf: '📄',
      doc: '📝', docx: '📝',
      xls: '📊', xlsx: '📊', csv: '📊',
      ppt: '📊', pptx: '📊',
      txt: '📃',
      rtf: '📃'
    };
    return icons[fileType] || '📎';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const loadAudio = async () => {
    if (audioUrl || !story?.audioKey) return;
    
    setLoadingAudio(true);
    try {
      const res = await storiesAPI.getAudioUrl(id);
      setAudioUrl(res.data.url);
    } catch (error) {
      console.error('Error loading audio:', error);
    } finally {
      setLoadingAudio(false);
    }
  };


  const loadVideo = async () => {
    if (videoUrl || !story?.videoKey) return;
    setLoadingVideo(true);
    try {
      const res = await storiesAPI.getVideoUrl(id);
      setVideoUrl(res.data.url);
    } catch (error) {
      console.error('Error loading video:', error);
    } finally {
      setLoadingVideo(false);
    }
  };

  const loadSynthesizedAudio = async () => {
    setLoadingSynthesized(true);
    try {
      const res = await voiceAPI.getSynthesizedUrl(id, activeVersion);
      setSynthesizedUrl(res.data.url);
    } catch (error) {
      console.log('No synthesized audio for this version');
      setSynthesizedUrl(null);
    } finally {
      setLoadingSynthesized(false);
    }
  };

  // === NEW: Comment functions ===
  const loadComments = async () => {
    setLoadingComments(true);
    try {
      const res = await storiesAPI.getComments(id);
      setComments(res.data.comments || []);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  // === Story Linking functions ===
  const loadLinkedStories = async () => {
    setLoadingLinked(true);
    try {
      const res = await storiesAPI.getLinkedStories(id);
      setLinkedStories(res.data.linkedStories || []);
    } catch (error) {
      console.error('Error loading linked stories:', error);
    } finally {
      setLoadingLinked(false);
    }
  };

  const handleSearchStories = async (query, storytellerFilter) => {
    setLinkSearchQuery(query);
    const filterValue = storytellerFilter !== undefined ? storytellerFilter : linkFilterStoryteller;
    
    if (!query.trim() && !filterValue) {
      setLinkSearchResults([]);
      return;
    }

    setSearchingStories(true);
    try {
      const familyId = story.familyCircleId?._id || story.familyCircleId;
      const params = {};
      if (query.trim()) params.search = query;
      if (filterValue) params.storytellerId = filterValue;
      
      const res = await storiesAPI.getByFamily(familyId, params);
      // Filter out current story and already-linked stories
      const linkedIds = linkedStories.map(ls => ls.storyId?._id || ls.storyId);
      const filtered = (res.data.stories || []).filter(
        s => s._id !== id && !linkedIds.includes(s._id)
      );
      setLinkSearchResults(filtered);
    } catch (error) {
      console.error('Search stories error:', error);
    } finally {
      setSearchingStories(false);
    }
  };

  const handleLinkFilterStoryteller = (value) => {
    setLinkFilterStoryteller(value);
    handleSearchStories(linkSearchQuery, value);
  };

  const handleLinkStory = async (targetStoryId) => {
    setLinkingStory(true);
    try {
      const res = await storiesAPI.linkStory(id, {
        targetStoryId,
        relationship: selectedLinkRelationship,
        note: linkNote
      });
      setLinkedStories(res.data.linkedStories || []);
      setShowLinkModal(false);
      setLinkSearchQuery('');
      setLinkSearchResults([]);
      setLinkNote('');
      setSelectedLinkRelationship('related');
      setLinkFilterStoryteller('');
    } catch (error) {
      console.error('Link story error:', error);
      alert(error.response?.data?.error || 'Failed to link stories');
    } finally {
      setLinkingStory(false);
    }
  };

  const handleUnlinkStory = async (targetStoryId) => {
    if (!window.confirm('Remove this story link?')) return;
    try {
      const res = await storiesAPI.unlinkStory(id, targetStoryId);
      setLinkedStories(res.data.linkedStories || []);
    } catch (error) {
      console.error('Unlink story error:', error);
      alert('Failed to unlink stories');
    }
  };

  const getRelationshipLabel = (rel) => {
    const labels = {
      'related': '🔗 Related',
      'continuation': '➡️ Continuation',
      'same_event': '📍 Same Event',
      'mentioned_in': '💬 Mentioned In',
      'response_to': '↩️ Response To',
      'parent_story': '👆 Parent Story',
      'child_story': '👇 Child Story'
    };
    return labels[rel] || '🔗 Related';
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || submittingComment) return;

    setSubmittingComment(true);
    try {
      const res = await storiesAPI.addComment(id, {
        text: commentText.trim(),
        version: activeVersion
      });
      setComments([res.data.comment, ...comments]);
      setCommentText('');
    } catch (error) {
      console.error('Add comment error:', error);
      alert(error.response?.data?.error || 'Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editCommentText.trim()) return;

    try {
      const res = await storiesAPI.editComment(id, commentId, {
        text: editCommentText.trim()
      });
      setComments(comments.map(c => 
        c._id === commentId ? res.data.comment : c
      ));
      setEditingCommentId(null);
      setEditCommentText('');
    } catch (error) {
      console.error('Edit comment error:', error);
      alert(error.response?.data?.error || 'Failed to edit comment');
    }
  };

  const handleDeleteComment = async () => {
    if (!deletingCommentId) return;
    
    setDeletingComment(true);
    try {
      await storiesAPI.deleteComment(id, deletingCommentId);
      setComments(comments.filter(c => c._id !== deletingCommentId));
      setShowDeleteCommentModal(false);
      setDeletingCommentId(null);
    } catch (error) {
      console.error('Delete comment error:', error);
      if (error.response?.status === 403) {
        alert('You can only delete your own comments. Vault Keepers can delete any comment.');
      } else {
        alert(error.response?.data?.error || 'Failed to delete comment');
      }
    } finally {
      setDeletingComment(false);
    }
  };

  const handleReaction = async (commentId, emoji) => {
    try {
      const res = await storiesAPI.reactToComment(id, commentId, emoji);
      setComments(comments.map(c => 
        c._id === commentId ? { ...c, reactions: res.data.reactions } : c
      ));
      setShowReactionPicker(null);
    } catch (error) {
      console.error('Reaction error:', error);
    }
  };

  const startEditComment = (comment) => {
    setEditingCommentId(comment._id);
    setEditCommentText(comment.text);
  };

  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditCommentText('');
  };

  const handleSaveTitle = async () => {
    if (!titleText.trim() || titleText.trim() === story.title) {
      setEditingTitle(false);
      return;
    }
    setSavingTitle(true);
    try {
      await storiesAPI.updateTitle(id, titleText.trim());
      setStory({ ...story, title: titleText.trim() });
      setEditingTitle(false);
    } catch (error) {
      console.error('Update title error:', error);
    } finally {
      setSavingTitle(false);
    }
  };

  const handleSaveCategory = async (value) => {
    const categoryValue = value === '__custom__' ? customCategory.trim() : value;
    if (!categoryValue && value !== '') return;
    
    setSavingCategory(true);
    try {
      await storiesAPI.updateCategory(id, categoryValue || null);
      setStory({ ...story, category: categoryValue || null });
      setEditingCategory(false);
      setCustomCategory('');
    } catch (error) {
      console.error('Update category error:', error);
      if (error.response?.status === 403) {
        alert('Only Storytellers and Vault Keepers can update categories. Please ask a Vault Keeper to upgrade your role.');
      } else {
        alert('Failed to update category');
      }
    } finally {
      setSavingCategory(false);
    }
  };

  const handleStartEditTags = () => {
    setEditableTags([...(story.metadata?.tags || [])]);
    setEditingTags(true);
    setTagInput('');
  };

  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (!tag || editableTags.includes(tag)) return;
    if (editableTags.length >= 20) {
      alert('Maximum 20 tags allowed');
      return;
    }
    setEditableTags([...editableTags, tag]);
    setTagInput('');
  };

  const handleRemoveTag = (tagToRemove) => {
    setEditableTags(editableTags.filter(t => t !== tagToRemove));
  };

  const handleSaveTags = async () => {
    setSavingTags(true);
    try {
      const res = await storiesAPI.updateTags(id, editableTags);
      setStory({ 
        ...story, 
        metadata: { ...story.metadata, tags: res.data.tags } 
      });
      setEditingTags(false);
    } catch (error) {
      console.error('Update tags error:', error);
      if (error.response?.status === 403) {
        alert('Only Storytellers and Vault Keepers can update tags. Please ask a Vault Keeper to upgrade your role.');
      } else {
        alert('Failed to update tags');
      }
    } finally {
      setSavingTags(false);
    }
  };

  const pollEnhancementStatus = (storyId) => {
    let attempts = 0;
    const MAX_ATTEMPTS = 60; // 3 minutes max
    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await storiesAPI.getOne(storyId);
        const status = res.data.story.status;
        if (status !== 'enhancing' || attempts >= MAX_ATTEMPTS) {
          clearInterval(interval);
          setStory(res.data.story);
          setEnhancing(false);
          if (res.data.story.enhancedVersions?.english) {
            setActiveVersion('english');
          } else if (res.data.story.enhancedVersions?.polished) {
            setActiveVersion('polished');
          }
        }
      } catch (err) {
        // Network error while polling (app backgrounded) — keep trying silently
        console.warn('Poll attempt failed, retrying...', err.message);
        if (attempts >= MAX_ATTEMPTS) {
          clearInterval(interval);
          setEnhancing(false);
        }
      }
    }, 3000);
    return interval;
  };

  // Resume polling if user returns to app while enhancement was in progress
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && id) {
        try {
          const res = await storiesAPI.getOne(id);
          const s = res.data.story;
          if (s.status === 'enhancing') {
            setEnhancing(true);
            pollEnhancementStatus(id);
          } else if (s.enhancedVersions?.polished || s.enhancedVersions?.english) {
            setStory(s);
            setEnhancing(false);
            if (s.enhancedVersions?.english) setActiveVersion('english');
            else if (s.enhancedVersions?.polished) setActiveVersion('polished');
          } else {
            setStory(s);
            setEnhancing(false);
          }
        } catch (err) {
          // Ignore — network may not be ready yet on resume
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [id]);

  const handleEnhance = async () => {
    setEnhancing(true);
    let polling = false;  // ADD THIS
    try {
      const res = await storiesAPI.enhance(id);
      setStory(res.data.story);
      if (res.data.story.enhancedVersions?.english) {
        setActiveVersion('english');
      } else {
        setActiveVersion('polished');
      }
    } catch (error) {
      console.error('Enhancement error:', error);
      const status = error.response?.status;
      const message = error.response?.data?.error || '';

      if (status === 403 && message.includes('limit reached')) {
        // Hard limit — stop spinner
        alert('Enhancement limit reached. This story has been enhanced 3 times.');
        setEnhancing(false);
        return;
      } else if (status === 403) {
        alert('Only Storytellers and Vault Keepers can enhance stories. Please ask a Vault Keeper to upgrade your role.');
        setEnhancing(false);
        return;
      } else if (status === 400 && message.includes('no transcript')) {
        alert('This story has no transcript to enhance. Please wait for transcription to complete.');
        setEnhancing(false);
        return;
      } else {
        // Network timeout or server still processing — switch to polling instead of showing error
        console.log('Enhancement timed out or errored — switching to polling mode...');
        try {
          // Check if server is still processing
          const checkRes = await storiesAPI.getOne(id);
          const currentStatus = checkRes.data.story.status;
          if (currentStatus === 'enhancing') {
            // Server is still working — poll until done
            console.log('Server still enhancing — polling for completion...');
            polling = true;
            pollEnhancementStatus(id);
          } else if (checkRes.data.story.enhancedVersions?.polished) {
            // Already done!
            setStory(checkRes.data.story);
            if (checkRes.data.story.enhancedVersions?.english) {
              setActiveVersion('english');
            } else {
              setActiveVersion('polished');
            }
          } else {
            alert('Enhancement failed. Please try again.');
          }
        } catch (checkErr) {
          alert('Enhancement failed. Please try again.');
        }
      }
      } finally {
        if (!polling) setEnhancing(false);
      }
  };

  const handleSynthesize = async () => {
    if (!selectedVoice) {
      alert('Please select a voice profile first');
      return;
    }

    setSynthesizing(true);
    try {
      await voiceAPI.synthesize(id, selectedVoice, activeVersion);
      const res = await storiesAPI.getOne(id);
      setStory(res.data.story);
      await loadSynthesizedAudio();
      setShowVoiceModal(false);
    } catch (error) {
      console.error('Synthesis error:', error);
      if (error.response?.status === 403) {
        alert('Only Storytellers and Vault Keepers can use voice synthesis. Please ask a Vault Keeper to upgrade your role.');
      } else {
        alert('Voice synthesis failed. Please try again.');
      }
    } finally {
      setSynthesizing(false);
    }
  };

  const handleCloneFromRecording = async (force = false) => {
    if (!cloneVoiceName.trim()) {
      alert('Please enter a name for the voice profile');
      return;
    }

    setCloningVoice(true);
    try {
      const familyId = story.familyCircleId?._id || story.familyCircleId;

      await voiceAPI.cloneFromStory(id, { name: cloneVoiceName.trim(), force });

      // Refresh voice profiles
      const voiceRes = await voiceAPI.getProfiles(familyId);
      setVoiceProfiles(voiceRes.data.voiceProfiles || []);

      setShowCloneModal(false);
      setShowRecloneConfirm(false);
      setCloneVoiceName('');
      alert('Voice profile created successfully! You can now generate AI narrations.');
    } catch (error) {
      console.error('Clone from recording error:', error);
      // Handle duplicate — offer to re-clone
      if (error.response?.status === 409) {
        setShowCloneModal(false);
        setShowRecloneConfirm(true);
      } else {
        alert('Failed to create voice clone: ' + (error.response?.data?.details || error.response?.data?.error || error.message));
      }
    } finally {
      setCloningVoice(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Validate file sizes (50MB for videos, 10MB for images)
    const MAX_VIDEO_SIZE = 50 * 1024 * 1024;
    const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
    
    for (const file of files) {
      const isVideo = file.type.startsWith('video/');
      const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
      const maxLabel = isVideo ? '50MB' : '10MB';
      if (file.size > maxSize) {
        alert(`${file.name} is too large. Maximum size is ${maxLabel}.`);
        return;
      }
    }

    setUploadingPhotos(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('photos', file);
      });

      await storiesAPI.uploadPhotos(id, formData);
      await loadPhotos();
      
      const res = await storiesAPI.getOne(id);
      setStory(res.data.story);
    } catch (error) {
      console.error('Photo upload error:', error);
      if (error.response?.status === 403) {
        alert('Only Storytellers and Vault Keepers can upload photos. Please ask a Vault Keeper to upgrade your role.');
      } else {
        alert('Failed to upload photos');
      }
    } finally {
      setUploadingPhotos(false);
      if (photoInputRef.current) {
        photoInputRef.current.value = '';
      }
    }
  };

  const handleDeleteStory = async () => {
    setDeletingStory(true);
    try {
      await storiesAPI.delete(id);
      navigate(-1);
    } catch (error) {
      console.error('Delete story error:', error);
      if (error.response?.status === 403) {
        alert('Only Storytellers and Vault Keepers can delete stories. Please ask a Vault Keeper to upgrade your role.');
      } else {
        alert('Failed to delete story');
      }
    } finally {
      setDeletingStory(false);
      setShowDeleteStoryModal(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!selectedPhoto) return;
    
    setDeletingPhoto(true);
    try {
      await storiesAPI.deletePhoto(id, selectedPhoto._id);
      setPhotos(photos.filter(p => p._id !== selectedPhoto._id));
      setSelectedPhoto(null);
      setShowDeletePhotoModal(false);
    } catch (error) {
      console.error('Delete photo error:', error);
      if (error.response?.status === 403) {
        alert('Only Storytellers and Vault Keepers can delete photos. Please ask a Vault Keeper to upgrade your role.');
      } else {
        alert('Failed to delete photo');
      }
    } finally {
      setDeletingPhoto(false);
    }
  };

  const handleEditCaption = () => {
    setCaptionText(selectedPhoto?.caption || '');
    setEditingCaption(true);
  };

  const handleSaveCaption = async () => {
    if (!selectedPhoto) return;
    
    setSavingCaption(true);
    try {
      await storiesAPI.updatePhoto(id, selectedPhoto._id, { caption: captionText });
      
      // Update local state
      setPhotos(photos.map(p => 
        p._id === selectedPhoto._id ? { ...p, caption: captionText } : p
      ));
      setSelectedPhoto({ ...selectedPhoto, caption: captionText });
      setEditingCaption(false);
    } catch (error) {
      console.error('Save caption error:', error);
      if (error.response?.status === 403) {
        alert('Only Storytellers and Vault Keepers can edit captions. Please ask a Vault Keeper to upgrade your role.');
      } else {
        alert('Failed to save caption');
      }
    } finally {
      setSavingCaption(false);
    }
  };

  const handleExportPDF = () => {
    const token = localStorage.getItem('token');
    const url = storiesAPI.exportPDF(id, { 
      version: activeVersion,
      includePhotos: 'true'
    });
    // Open in new tab with auth
    window.open(`${url}&token=${token}`, '_blank');
  };

  const handleSaveTimeCapsule = async () => {
    setSavingTimeCapsule(true);
    try {
      await storiesAPI.setTimeCapsule(id, {
        enabled: timeCapsuleEnabled,
        unlockDate: timeCapsuleEnabled ? unlockDate : null,
        message: timeCapsuleMessage
      });
      
      // Close modal and reset spinner immediately
      setSavingTimeCapsule(false);
      setShowTimeCapsuleModal(false);

      if (timeCapsuleEnabled) {
        // Show success message and navigate back
        const formattedDate = new Date(unlockDate).toLocaleDateString('en-US', {
          year: 'numeric', month: 'long', day: 'numeric'
        });
        alert(`🔒 Time Capsule set! This story is now sealed until ${formattedDate}.`);
        navigate(-1);
      } else {
        // Capsule disabled — stay on page and reload story
        try {
          const res = await storiesAPI.getOne(id);
          setStory(res.data.story);
        } catch (reloadErr) {
          console.warn('Story reload after time capsule save failed:', reloadErr);
        }
      }
    } catch (error) {
      console.error('Time capsule error:', error);
      alert(error.response?.data?.error || 'Failed to set time capsule');
      setSavingTimeCapsule(false);
    }
  };

  const handleReassign = async () => {
    if (!reassignTargetId) return;
    setReassigning(true);
    try {
      const isTreeMember = treeOnlyMembers.some(m => m._id === reassignTargetId);
      const res = await storiesAPI.reassign(id, reassignTargetId, isTreeMember ? 'tree' : 'user');
      setStory(res.data.story);
      setShowReassignModal(false);
      setReassignTargetId('');
      alert('✅ Story reassigned successfully');
    } catch (error) {
      console.error('Reassign error:', error);
      alert(error.response?.data?.error || 'Failed to reassign story');
    } finally {
      setReassigning(false);
    }
  };

  const formatUnlockDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isTimeCapsuleLocked = story?.timeCapsule?.enabled && 
    new Date(story.timeCapsule.unlockDate) > new Date();

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCommentDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getVersionContent = () => {
    if (!story) return '';
    
    switch (activeVersion) {
      case 'raw':
        return story.enhancedVersions?.raw || story.rawTranscript || '';
      case 'polished':
        return story.enhancedVersions?.polished || '';
      case 'literary':
        return story.enhancedVersions?.literary || '';
      case 'english':
        return story.enhancedVersions?.english || '';
      default:
        return story.rawTranscript || '';
    }
  };

  const getVersionLabel = (version) => {
    switch (version) {
      case 'raw': return 'Original';
      case 'polished': return 'Polished';
      case 'literary': return 'Literary';
      case 'english': return 'English';
      default: return 'General';
    }
  };

  // Group reactions by emoji for display
  const groupReactions = (reactions) => {
    const groups = {};
    (reactions || []).forEach(r => {
      if (!groups[r.emoji]) {
        groups[r.emoji] = { emoji: r.emoji, users: [], hasCurrentUser: false };
      }
      groups[r.emoji].users.push(r.userId);
      if ((r.userId?._id || r.userId) === currentUser._id) {
        groups[r.emoji].hasCurrentUser = true;
      }
    });
    return Object.values(groups);
  };

  const hasEnhancedVersions = story?.enhancedVersions?.polished || story?.enhancedVersions?.literary;
  // Check if voice already cloned — first by synthesizedAudio link, then by name match
  const linkedVoiceProfileId = 
    story?.synthesizedAudio?.literary?.voiceProfileId ||
    story?.synthesizedAudio?.polished?.voiceProfileId ||
    story?.synthesizedAudio?.raw?.voiceProfileId;

  const linkedVoiceProfile = linkedVoiceProfileId
    ? voiceProfiles.find(vp => vp._id?.toString() === linkedVoiceProfileId?.toString())
    : null;

  const storytellerVoiceExists = !!linkedVoiceProfile || voiceProfiles.some(
    vp => vp.name?.toLowerCase() === (story?.storytellerName || story?.title || '').toLowerCase()
  );

  // The actual matched profile (for display)
  const matchedVoiceProfile = linkedVoiceProfile || voiceProfiles.find(
    vp => vp.name?.toLowerCase() === (story?.storytellerName || story?.title || '').toLowerCase()
  );
  const hasSynthesizedAudio = story?.synthesizedAudio?.[activeVersion]?.key;

  if (loading) {
    return (
      <div className="story-detail-loading">
        <div className="spinner-large"></div>
        <p>Loading story...</p>
      </div>
    );
  }

  if (!story) {
    return (
      <div className="story-detail-error">
        <p>Story not found</p>
        <button onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  return (
    <div className="story-detail">
      <header className="story-detail-header">
        <button className="btn-back" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <div className="header-info">
          {editingTitle ? (
            <div className="title-edit">
              <input
                type="text"
                value={titleText}
                onChange={(e) => setTitleText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveTitle();
                  if (e.key === 'Escape') setEditingTitle(false);
                }}
                autoFocus
                className="title-edit-input"
              />
              <button className="btn-title-save" onClick={handleSaveTitle} disabled={savingTitle}>
                {savingTitle ? '...' : '✓'}
              </button>
              <button className="btn-title-cancel" onClick={() => setEditingTitle(false)}>✕</button>
            </div>
          ) : (
            <h1 onClick={() => { if (!isTimeCapsuleLocked) { setTitleText(story.title); setEditingTitle(true); } }} className={isTimeCapsuleLocked ? '' : 'editable-title'} title={isTimeCapsuleLocked ? '' : 'Click to edit title'}>
              {isTimeCapsuleLocked ? '🔒 ' : ''}{story.title} {!isTimeCapsuleLocked && <span className="edit-hint">✏️</span>}
            </h1>
          )}
          <div className="story-meta-header">
            <span>{formatDate(story.createdAt)}</span>
            {story.audioDuration > 0 && (
              <span>• {formatDuration(story.audioDuration)}</span>
            )}
            <span>• {story.storytellerType === 'tree' ? story.storytellerName : story.storytellerId?.displayName}</span>
          </div>
        </div>
        <div className="story-header-actions">
          {(story.storytellerId?._id || story.storytellerId) === currentUser._id && (
          <button 
            className={`btn-time-capsule ${story.timeCapsule?.enabled ? 'active' : ''}`}
            onClick={() => setShowTimeCapsuleModal(true)}
            title="Time Capsule"
          >
            {story.timeCapsule?.enabled ? '🔒' : '⏰'}
          </button>
          )}
          {!isTimeCapsuleLocked && (
          <button 
            className="btn-export-pdf"
            onClick={handleExportPDF}
            title="Export as PDF"
          >
            📄 PDF
          </button>
          )}
          <button 
            className="btn-delete-story"
            onClick={() => setShowDeleteStoryModal(true)}
            title="Delete Story"
            style={{ display: canEdit ? 'inline-flex' : 'none' }}
          >
            🗑️
          </button>
          {isVaultKeeper && (
            <button
              className="btn-reassign-story"
              onClick={() => {
                setReassignTargetId('');
                setShowReassignModal(true);
              }}
              title="Reassign Story"
            >
              👤 Reassign
            </button>
          )}
        </div>
      </header>

      <main className="story-detail-main">
        {/* Time Capsule Banner */}
        {story.timeCapsule?.enabled && (
          <div className={`time-capsule-banner ${isTimeCapsuleLocked ? 'locked' : 'unlocked'}`}>
            {isTimeCapsuleLocked ? (
              <>
                <span className="capsule-icon">🔒</span>
                <div className="capsule-info">
                  <strong>Time Capsule — Sealed</strong>
                  <p>This story will unlock on {formatUnlockDate(story.timeCapsule.unlockDate)}</p>
                  {story.timeCapsule.message && (
                    <p className="capsule-message">"{story.timeCapsule.message}"</p>
                  )}
                </div>
              </>
            ) : (
              <>
                <span className="capsule-icon">🎉</span>
                <div className="capsule-info">
                  <strong>Time Capsule Unlocked!</strong>
                  <p>This story was sealed until {formatUnlockDate(story.timeCapsule.unlockDate)}</p>
                </div>
              </>
            )}
          </div>
        )}

        {/* === LOCKED: Hide all content when time capsule is sealed === */}
        {isTimeCapsuleLocked ? (
          <div className="time-capsule-locked-content" style={{
            textAlign: 'center',
            padding: '60px 24px',
            color: 'rgba(255,255,255,0.4)',
            fontSize: '15px',
            lineHeight: '1.8'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>🔒</div>
            <p style={{ fontSize: '18px', fontWeight: '600', color: 'rgba(255,255,255,0.6)', marginBottom: '8px' }}>
              This story is sealed
            </p>
            <p>
              It will be available to read on <strong style={{ color: '#fbbf24' }}>{formatUnlockDate(story.timeCapsule.unlockDate)}</strong>
            </p>
            {story.timeCapsule.message && (
              <p style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.5)', marginTop: '16px', fontSize: '14px' }}>
                "{story.timeCapsule.message}"
              </p>
            )}
          </div>
        ) : (
          <>
        {/* Video Player — shown for video stories */}
{story.isVideoStory && story.videoKey && (
          <div className="audio-section" style={{ marginBottom: '16px' }}>
            <div className="audio-section-header">
              <span className="audio-label">🎬 Video Story</span>
              {story.videoDuration > 0 && (
                <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>
                  {Math.floor(story.videoDuration / 60)}:{String(story.videoDuration % 60).padStart(2,'0')}
                </span>
              )}
              <button
                onClick={async () => {
                  try {
                    const res = await storiesAPI.getVideoUrl(id);
                    const a = document.createElement('a');
                    a.href = res.data.url;
                    a.download = `${(story.title || 'video').replace(/[^a-zA-Z0-9\s-]/g, '')}.mp4`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  } catch (err) {
                    console.error('Download error:', err);
                  }
                }}
                style={{ marginLeft: 'auto', fontSize: '12px', color: 'rgba(255,255,255,0.4)', background: 'none', border: 'none', cursor: 'pointer', padding: '0' }}
              >
                ⬇️ Download
              </button>
            </div>
            {videoUrl || story.hlsKey ? (
              <HLSVideoPlayer
                hlsUrl={story.hlsKey ? `${process.env.REACT_APP_CDN_URL}/${story.hlsKey}` : null}
                fallbackUrl={videoUrl}
                poster={story.videoThumbnailKey ? `${process.env.REACT_APP_CDN_URL}/${story.videoThumbnailKey}` : null}
              />
            ) : (
              <button
                className="btn-load-audio"
                onClick={loadVideo}
                disabled={loadingVideo}
              >
                {loadingVideo ? 'Loading...' : '▶️ Load Video'}
              </button>
            )}
          </div>
        )}

        {/* Synthesized Audio Player */}
        {hasSynthesizedAudio && (
          <div className="audio-section synthesized-audio">
            <div className="audio-section-header">
              <span className="audio-label">🎙️ AI Voice Narration ({activeVersion})</span>
            </div>
            {synthesizedUrl ? (
              <audio src={synthesizedUrl} controls className="audio-player" />
            ) : loadingSynthesized ? (
              <div className="loading-audio">Loading synthesized audio...</div>
            ) : null}
          </div>
        )}

        {/* Original Audio Player */}
        {story.audioKey && (
          <div className="audio-section">
            <div className="audio-section-header">
              <span className="audio-label">🎤 Original Recording</span>
              {audioUrl && (
                <button 
                  className="btn-download-audio"
                  title="Download original audio"
                  onClick={async () => {
                    try {
                      const response = await fetch(audioUrl);
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${(story.title || 'recording').replace(/[^a-zA-Z0-9\s-]/g, '')}.webm`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      window.URL.revokeObjectURL(url);
                    } catch (err) {
                      console.error('Download error:', err);
                      window.open(audioUrl, '_blank');
                    }
                  }}
                >
                  ⬇️ Save
                </button>
              )}
            </div>
            {audioUrl ? (
              <audio src={audioUrl} controls preload="metadata" className="audio-player" />
            ) : (
              <button 
                className="btn-load-audio"
                onClick={loadAudio}
                disabled={loadingAudio}
              >
                {loadingAudio ? 'Loading...' : '▶️ Load Original Audio'}
              </button>
            )}
          </div>
        )}

        {/* Version Tabs */}
        <div className="version-tabs">
          <button 
            className={`version-tab ${activeVersion === 'raw' ? 'active' : ''}`}
            onClick={() => setActiveVersion('raw')}
          >
            📝 Original
            {story.synthesizedAudio?.raw?.key && <span className="synth-badge">🔊</span>}
          </button>
          <button 
            className={`version-tab ${activeVersion === 'polished' ? 'active' : ''}`}
            onClick={() => setActiveVersion('polished')}
            disabled={!story.enhancedVersions?.polished}
          >
            ✨ Polished
            {story.synthesizedAudio?.polished?.key && <span className="synth-badge">🔊</span>}
          </button>
          <button 
            className={`version-tab ${activeVersion === 'literary' ? 'active' : ''}`}
            onClick={() => setActiveVersion('literary')}
            disabled={!story.enhancedVersions?.literary}
          >
            📖 Literary
            {story.synthesizedAudio?.literary?.key && <span className="synth-badge">🔊</span>}
          </button>
          {story.enhancedVersions?.english && (
            <button 
              className={`version-tab ${activeVersion === 'english' ? 'active' : ''}`}
              onClick={() => setActiveVersion('english')}
            >
              🌐 English
            </button>
          )}
        </div>

        {/* Story Content */}
        <div className="story-content">
          {activeVersion === 'literary' && story.enhancedVersions?.literary ? (
            <div 
              className="literary-content"
              dangerouslySetInnerHTML={{ 
                __html: story.enhancedVersions.literary
                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                  .replace(/\n\n/g, '</p><p>')
                  .replace(/\n/g, '<br/>')
              }}
            />
          ) : (
            <div className="transcript-content">
              {getVersionContent().split('\n\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          )}
        </div>

        {/* Category & Tags */}
        <div className="story-metadata">
          {/* Category */}
          <div className="category-section">
            <span className="meta-label">📂 Category:</span>
            {editingCategory ? (
              <div className="category-edit">
                <select
                  value={selectedCategory}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    if (e.target.value !== '__custom__' && e.target.value !== '') {
                      handleSaveCategory(e.target.value);
                    }
                  }}
                  disabled={savingCategory}
                >
                  <option value="">None</option>
                  {PRESET_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                  <option value="__custom__">+ Custom...</option>
                </select>
                {selectedCategory === '__custom__' && (
                  <div className="custom-category-input">
                    <input
                      type="text"
                      value={customCategory}
                      onChange={(e) => setCustomCategory(e.target.value)}
                      placeholder="Enter custom category"
                      maxLength={50}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && customCategory.trim()) handleSaveCategory('__custom__');
                        if (e.key === 'Escape') setEditingCategory(false);
                      }}
                    />
                    <button className="btn-sm btn-primary" onClick={() => handleSaveCategory('__custom__')} disabled={!customCategory.trim() || savingCategory}>
                      {savingCategory ? '...' : 'Save'}
                    </button>
                  </div>
                )}
                <button className="btn-cancel-sm" onClick={() => setEditingCategory(false)}>✕</button>
              </div>
            ) : (
              <span 
                className={`category-value ${canEdit ? 'editable' : ''}`}
                onClick={() => { if (canEdit) { setSelectedCategory(story.category || ''); setEditingCategory(true); } }}
                title={canEdit ? 'Click to edit' : ''}
              >
                {story.category || 'Uncategorized'} {canEdit && <span className="edit-hint">✏️</span>}
              </span>
            )}
          </div>

          {/* Tags */}
          <div className="tags-section">
            <div className="tags-header">
              <span className="meta-label">🏷️ Tags:</span>
              {!editingTags && canEdit && (
                <button className="btn-edit-tags" onClick={handleStartEditTags}>Edit</button>
              )}
            </div>
            {editingTags ? (
              <div className="tags-edit">
                <div className="tags-edit-list">
                  {editableTags.map((tag, i) => (
                    <span key={i} className="tag editable-tag">
                      {tag}
                      <button className="tag-remove" onClick={() => handleRemoveTag(tag)}>✕</button>
                    </span>
                  ))}
                </div>
                <div className="tag-input-row">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="Add a tag..."
                    maxLength={30}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); }
                    }}
                  />
                  <button className="btn-sm btn-secondary" onClick={handleAddTag} disabled={!tagInput.trim()}>Add</button>
                </div>
                <div className="tags-edit-actions">
                  <button className="btn-sm btn-secondary" onClick={() => setEditingTags(false)}>Cancel</button>
                  <button className="btn-sm btn-primary" onClick={handleSaveTags} disabled={savingTags}>
                    {savingTags ? 'Saving...' : 'Save Tags'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="tags">
                {story.metadata?.tags?.length > 0 ? (
                  story.metadata.tags.map((tag, i) => (
                    <span key={i} className="tag">{tag}</span>
                  ))
                ) : (
                  <span className="no-tags" onClick={() => { if (canEdit) handleStartEditTags(); }}>{canEdit ? 'No tags yet — click to add' : 'No tags yet'}</span>
                )}
              </div>
            )}
          </div>

          {/* People Mentioned */}
          {story.metadata?.peopleMentioned?.length > 0 && (
            <div className="people-mentioned">
              <strong>People mentioned:</strong> {story.metadata.peopleMentioned.join(', ')}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {canEdit && (
        <div className="action-buttons">
          {story.rawTranscript && (
            <button 
              className="btn-enhance"
              onClick={() => {
                if (hasEnhancedVersions) {
                  if ((story.enhancementCount || 0) >= 3) {
                    alert('Enhancement limit reached. This story has been enhanced 3 times.');
                    return;
                  }
                  setShowReenhanceModal(true);
                } else {
                  handleEnhance();
                }
              }}
              disabled={enhancing || (hasEnhancedVersions && (story.enhancementCount || 0) >= 3)}
            >
              {enhancing ? (
                <>
                  <span className="spinner"></span>
                  Enhancing...
                </>
              ) : hasEnhancedVersions ? (
                (story.enhancementCount || 0) >= 3
                  ? '✨ Re-enhance (limit reached)'
                  : `✨ Re-enhance (${3 - (story.enhancementCount || 0)} left)`
              ) : (
                '✨ Enhance with AI'
              )}
            </button>
          )}

          {hasEnhancedVersions && voiceProfiles.length > 0 && !hasSynthesizedAudio && (
            <button 
              className="btn-synthesize"
              onClick={() => setShowVoiceModal(true)}
            >
              🎙️ Generate Voice Narration
            </button>
          )}

          {hasEnhancedVersions && voiceProfiles.length === 0 && (
            <div className="no-voice-hint">
              <p>💡 Create a voice profile in the Members tab to generate AI narration</p>
            </div>
          )}

          {/* Clone voice from this recording */}
          {story?.audioKey && (
            storytellerVoiceExists ? (
              <button
                className="btn-clone-voice btn-clone-voice--exists"
                onClick={() => {
                  setCloneVoiceName(matchedVoiceProfile?.name || story.storytellerName || story.title || '');
                  setShowRecloneConfirm(true);
                }}
              >
                ✅ Voice Cloned{matchedVoiceProfile?.name ? ` · ${matchedVoiceProfile.name}` : ''} · Re-clone?
              </button>
            ) : (
              <button
                className="btn-clone-voice"
                onClick={() => {
                  setCloneVoiceName(story.storytellerName || story.title || '');
                  setShowCloneModal(true);
                }}
              >
                🎤 Clone Voice from This Recording
              </button>
            )
          )}
        </div>
        )}

        {/* === Linked Stories Section === */}
        <div className="linked-stories-section">
          <div className="linked-stories-header">
            <h3>🔗 Linked Stories {linkedStories.length > 0 && <span className="link-count">({linkedStories.length})</span>}</h3>
            <button 
              className="btn-link-story"
              onClick={() => setShowLinkModal(true)}
            >
              + Link Story
            </button>
          </div>

          {loadingLinked ? (
            <div className="linked-loading"><div className="spinner"></div></div>
          ) : linkedStories.length > 0 ? (
            <div className="linked-stories-list">
              {linkedStories.map((link, index) => (
                <div key={index} className="linked-story-card" onClick={() => navigate(`/story/${link.storyId?._id}`)}>
                  <div className="linked-story-info">
                    <span className="linked-relationship">{getRelationshipLabel(link.relationship)}</span>
                    <h4>{link.storyId?.title || 'Untitled Story'}</h4>
                    <span className="linked-meta">
                      {link.storyId?.storytellerId?.displayName} 
                      {link.storyId?.createdAt && ` • ${new Date(link.storyId.createdAt).toLocaleDateString()}`}
                    </span>
                    {link.note && <p className="linked-note">"{link.note}"</p>}
                  </div>
                  <button 
                    className="btn-unlink"
                    onClick={(e) => { e.stopPropagation(); handleUnlinkStory(link.storyId?._id); }}
                    title="Remove link"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-linked-stories">No linked stories yet. Link related stories to build connections between memories.</p>
          )}
        </div>

{/* Photo Gallery */}
        <div className="photos-section">
          <div className="photos-header">
            <h3>📷 Photos & Videos</h3>
            {canEdit && (
            <>
            <button 
              className="btn-add-photo"
              onClick={() => photoInputRef.current?.click()}
              disabled={uploadingPhotos}
            >
              {uploadingPhotos ? 'Uploading...' : '+ Add Media'}
            </button>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handlePhotoUpload}
              disabled={uploadingPhotos}
              style={{ display: 'none' }}
            />
            </>
            )}
          </div>
          
          {photos.length > 0 ? (
            <div className="photos-grid">
              {photos.map((photo) => {
                const isVideo = photo.url && (
                  photo.url.match(/\.(mp4|mov|avi|wmv|mkv|3gp|webm)(\?|$)/i) ||
                  photo.mimeType?.startsWith('video/')
                );
                return (
                  <div 
                    key={photo._id} 
                    className={`photo-thumb ${isVideo ? 'video-thumb' : ''}`}
                    onClick={() => setSelectedPhoto({ ...photo, isVideo })}
                  >
                    {isVideo ? (
                      window.Capacitor?.isNativePlatform() ? (
                        <div className="video-placeholder">
                          <span className="video-placeholder-icon">🎬</span>
                          <span className="video-placeholder-label">Video</span>
                          <div className="video-play-overlay">▶</div>
                        </div>
                      ) : (
                        <>
                          <video src={photo.url} preload="metadata" muted />
                          <div className="video-play-overlay">▶</div>
                        </>
                      )
                    ) : (
                      <img src={photo.url} alt={photo.caption || 'Story photo'} />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-photos">
              <p>No photos yet. Add photos or videos to bring this story to life!</p>
            </div>
          )}
        </div>

        {/* Attachments Section */}
        <div className="attachments-section">
          <div className="attachments-header">
            <h3>📎 Documents & Files</h3>
            {canEdit && (
            <>
            <button 
              className="btn-add-attachment"
              onClick={() => attachmentInputRef.current?.click()}
              disabled={uploadingAttachments}
            >
              {uploadingAttachments ? 'Uploading...' : '+ Add Files'}
            </button>
            <input
              ref={attachmentInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf"
              multiple
              onChange={handleAttachmentUpload}
              disabled={uploadingAttachments}
              style={{ display: 'none' }}
            />
            </>
            )}
          </div>

          {attachments.length > 0 ? (
            <div className="attachments-grid">
              {attachments.map((att) => (
                <div key={att._id} className="attachment-card">
                  <div className="attachment-icon">{getFileIcon(att.fileType)}</div>
                  <div className="attachment-info">
                    <span className="attachment-name" title={att.fileName}>
                      {att.fileName.length > 25 ? att.fileName.substring(0, 22) + '...' : att.fileName}
                    </span>
                    <span className="attachment-meta">
                      {att.fileType.toUpperCase()} • {formatFileSize(att.fileSize)}
                    </span>
                  </div>
                  <div className="attachment-actions">
                    <a href={att.url} target="_blank" rel="noopener noreferrer" className="btn-download-att" title="Download">⬇️</a>
                    {canEdit && <button className="btn-delete-att" title="Delete" onClick={() => handleDeleteAttachment(att._id)}>🗑️</button>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-attachments">
              <p>No documents yet. Attach PDFs, Word docs, spreadsheets, and more.</p>
            </div>
          )}
        </div>

        {/* === NEW: Comments Section === */}
        <div className="comments-section">
          <div className="comments-header">
            <h3>💬 Family Comments {comments.length > 0 && <span className="comment-count">({comments.length})</span>}</h3>
          </div>

          {/* Add Comment Form */}
          <form className="comment-form" onSubmit={handleAddComment}>
            <div className="comment-input-wrapper">
              <div className="comment-avatar">
                {currentUser.displayName?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <textarea
                ref={commentInputRef}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Share your thoughts about this story..."
                rows={2}
                maxLength={2000}
                disabled={submittingComment}
              />
            </div>
            <div className="comment-form-footer">
              <span className="comment-version-tag">
                on {getVersionLabel(activeVersion)} version
              </span>
              <button 
                type="submit" 
                className="btn-comment-submit"
                disabled={!commentText.trim() || submittingComment}
              >
                {submittingComment ? 'Posting...' : 'Post'}
              </button>
            </div>
          </form>

          {/* Comments List */}
          {loadingComments ? (
            <div className="comments-loading">
              <div className="spinner"></div>
              <p>Loading comments...</p>
            </div>
          ) : comments.length > 0 ? (
            <div className="comments-list">
              {comments.map((comment) => (
                <div key={comment._id} className="comment-item">
                  <div className="comment-avatar">
                    {comment.userId?.displayName?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="comment-body">
                    <div className="comment-meta">
                      <span className="comment-author">{comment.userId?.displayName || 'Unknown'}</span>
                      <span className="comment-time">{formatCommentDate(comment.createdAt)}</span>
                      {comment.editedAt && <span className="comment-edited">(edited)</span>}
                      {comment.version && comment.version !== 'general' && (
                        <span className="comment-version-badge">{getVersionLabel(comment.version)}</span>
                      )}
                    </div>

                    {editingCommentId === comment._id ? (
                      <div className="comment-edit-form">
                        <textarea
                          value={editCommentText}
                          onChange={(e) => setEditCommentText(e.target.value)}
                          rows={2}
                          maxLength={2000}
                          autoFocus
                        />
                        <div className="comment-edit-actions">
                          <button 
                            className="btn-sm btn-secondary"
                            onClick={cancelEditComment}
                          >
                            Cancel
                          </button>
                          <button 
                            className="btn-sm btn-primary"
                            onClick={() => handleEditComment(comment._id)}
                            disabled={!editCommentText.trim()}
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="comment-text">{comment.text}</p>
                    )}

                    {/* Reactions */}
                    <div className="comment-reactions">
                      {groupReactions(comment.reactions).map((group) => (
                        <button
                          key={group.emoji}
                          className={`reaction-badge ${group.hasCurrentUser ? 'active' : ''}`}
                          onClick={() => handleReaction(comment._id, group.emoji)}
                          title={group.users.map(u => u?.displayName || 'Someone').join(', ')}
                        >
                          {group.emoji} {group.users.length}
                        </button>
                      ))}
                      
                      <button 
                        className="btn-add-reaction"
                        onClick={() => setShowReactionPicker(
                          showReactionPicker === comment._id ? null : comment._id
                        )}
                      >
                        +
                      </button>

                      {showReactionPicker === comment._id && (
                        <div className="reaction-picker">
                          {REACTION_EMOJIS.map((emoji) => (
                            <button
                              key={emoji}
                              className="reaction-option"
                              onClick={() => handleReaction(comment._id, emoji)}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Comment Actions (author can edit & delete, vault keepers can delete) */}
                    {((comment.userId?._id || comment.userId) === currentUser._id || isVaultKeeper) && (
                      <div className="comment-actions">
                        {(comment.userId?._id || comment.userId) === currentUser._id && (
                        <button 
                          className="btn-comment-action"
                          onClick={() => startEditComment(comment)}
                        >
                          Edit
                        </button>
                        )}
                        <button 
                          className="btn-comment-action delete"
                          onClick={() => {
                            setDeletingCommentId(comment._id);
                            setShowDeleteCommentModal(true);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-comments">
              <p>No comments yet. Be the first to share your thoughts!</p>
            </div>
          )}
        </div>
          </>
        )}
      </main>

      {/* Voice Synthesis Modal */}
      {showVoiceModal && (
        <div className="modal-overlay" onClick={() => setShowVoiceModal(false)}>
          <div className="modal-content voice-modal" onClick={(e) => e.stopPropagation()}>
            <h2>🎙️ Generate Voice Narration</h2>
            <p className="modal-description">
              Create an AI-narrated version of the <strong>{activeVersion}</strong> text using a cloned voice.
            </p>

            <div className="form-group">
              <label>Select Voice Profile</label>
              <select 
                value={selectedVoice || ''} 
                onChange={(e) => setSelectedVoice(e.target.value)}
              >
                {voiceProfiles.map((profile) => (
                  <option key={profile._id} value={profile._id}>
                    {profile.name || profile.userId?.displayName || 'Unnamed Voice'} {profile.userId?.displayName ? `(by ${profile.userId.displayName})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="modal-actions">
              <button 
                className="btn-secondary"
                onClick={() => setShowVoiceModal(false)}
                disabled={synthesizing}
              >
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={handleSynthesize}
                disabled={synthesizing || !selectedVoice}
              >
                {synthesizing ? (
                  <>
                    <span className="spinner"></span>
                    Generating...
                  </>
                ) : (
                  'Generate Narration'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Re-clone Confirmation Modal */}
      {showRecloneConfirm && (
        <div className="modal-overlay" onClick={() => setShowRecloneConfirm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px', textAlign: 'center', padding: '36px 28px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h2 style={{ marginBottom: '8px' }}>Replace Voice Profile?</h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', lineHeight: '1.7', marginBottom: '24px' }}>
              A voice profile for <strong style={{ color: '#a78bfa' }}>{matchedVoiceProfile?.name || cloneVoiceName}</strong> already exists.
              Re-cloning will <strong>delete the existing profile</strong> and create a new one from this recording.
              This uses ElevenLabs credits and cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button className="btn-secondary" onClick={() => setShowRecloneConfirm(false)} style={{ flex: 1 }}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={() => handleCloneFromRecording(true)}
                disabled={cloningVoice}
                style={{ flex: 1, background: 'linear-gradient(135deg, #dc2626, #b91c1c)' }}
              >
                {cloningVoice ? <><span className="spinner"></span> Re-cloning...</> : '🔄 Yes, Re-clone'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clone Voice from Recording Modal */}
      {showCloneModal && (
        <div className="modal-overlay" onClick={() => setShowCloneModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>🎤 Clone Voice from Recording</h2>
            <p className="modal-description">
              Use this story's audio recording as a voice sample for ElevenLabs voice cloning. 
              For best results, recordings should be 30 seconds to 5 minutes of clear speech.
            </p>

            <div className="form-group">
              <label>Voice Profile Name</label>
              <input
                type="text"
                value={cloneVoiceName}
                onChange={(e) => setCloneVoiceName(e.target.value)}
                placeholder="e.g., Dad's Voice, Grandma Maria"
                required
              />
            </div>

            <div className="modal-actions">
              <button 
                className="btn-secondary"
                onClick={() => setShowCloneModal(false)}
                disabled={cloningVoice}
              >
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={handleCloneFromRecording}
                disabled={cloningVoice || !cloneVoiceName.trim()}
              >
                {cloningVoice ? (
                  <>
                    <span className="spinner"></span>
                    Creating Voice Clone...
                  </>
                ) : (
                  'Create Voice Clone'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Lightbox */}
      {selectedPhoto && (
        <div className="modal-overlay lightbox" onClick={() => { setSelectedPhoto(null); setEditingCaption(false); }}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => { setSelectedPhoto(null); setEditingCaption(false); }}>×</button>
            {selectedPhoto.isVideo ? (
              <video 
                src={selectedPhoto.url} 
                controls 
                autoPlay
                style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: '8px' }}
              />
            ) : (
              <img src={selectedPhoto.url} alt={selectedPhoto.caption || 'Story photo'} />
            )}
            
            <div className="lightbox-caption-section">
              {editingCaption ? (
                <div className="caption-edit">
                  <input
                    type="text"
                    value={captionText}
                    onChange={(e) => setCaptionText(e.target.value)}
                    placeholder="Add a caption..."
                    autoFocus
                  />
                  <div className="caption-actions">
                    <button 
                      className="btn-secondary btn-sm"
                      onClick={() => setEditingCaption(false)}
                      disabled={savingCaption}
                    >
                      Cancel
                    </button>
                    <button 
                      className="btn-primary btn-sm"
                      onClick={handleSaveCaption}
                      disabled={savingCaption}
                    >
                      {savingCaption ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="caption-display" onClick={() => { if (canEdit) handleEditCaption(); }}>
                  {selectedPhoto.caption ? (
                    <p>{selectedPhoto.caption}</p>
                  ) : canEdit ? (
                    <p className="caption-placeholder">Click to add caption...</p>
                  ) : null}
                </div>
              )}
            </div>

            {canEdit && (
            <button 
              className="btn-delete-photo"
              onClick={() => setShowDeletePhotoModal(true)}
            >
              🗑️ Delete {selectedPhoto.isVideo ? 'Video' : 'Photo'}
            </button>
            )}
          </div>
        </div>
      )}

      {/* Reassign Story Modal */}
      {showReassignModal && (
        <div className="modal-overlay" onClick={() => setShowReassignModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>👤 Reassign Story</h3>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', marginBottom: '16px' }}>
              Select the family member this story belongs to. The story will be credited to them going forward.
            </p>
            <div style={{ marginBottom: '12px', fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>
              Currently credited to: <strong style={{ color: 'rgba(255,255,255,0.8)' }}>{story.storytellerType === 'tree' ? story.storytellerName : story.storytellerId?.displayName}</strong>
            </div>
            <select
              value={reassignTargetId}
              onChange={(e) => setReassignTargetId(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '14px',
                marginBottom: '20px',
                cursor: 'pointer'
              }}
            >
              <option value="">— Select a member —</option>
              {/* Account holders */}
              {circleMembers.filter(m => {
                  const mId = m.userId?._id || m.userId;
                  const storytellerId = story.storytellerId?._id || story.storytellerId;
                  // Hide current storyteller (unless it's a tree member now)
                  if (story.storytellerType !== 'tree' && mId?.toString() === storytellerId?.toString()) return false;
                  return true;
                }).map(m => {
                  const mId = m.userId?._id || m.userId;
                  const name = m.displayName || m.userId?.displayName || 'Unknown';
                  return (
                    <option key={mId} value={mId}>
                      {name}{mId?.toString() === currentUser._id ? ' (You)' : ''}
                    </option>
                  );
                })
              }
              {/* Tree-only members (no account) */}
              {treeOnlyMembers.length > 0 && (
                <>
                  <option disabled>— Family Tree (no account) —</option>
                  {treeOnlyMembers
                    .filter(m => story.storytellerType !== 'tree' || m._id !== story.treeOnlyStorytellerId?.toString())
                    .map(m => (
                      <option key={m._id} value={m._id}>
                        {m.name}{m.isDeceased ? ' †' : ''}
                      </option>
                    ))
                  }
                </>
              )}
            </select>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowReassignModal(false)}
                disabled={reassigning}
              >
                Cancel
              </button>
              <button
                className="btn-confirm-reassign"
                onClick={handleReassign}
                disabled={!reassignTargetId || reassigning}
              >
                {reassigning ? 'Reassigning...' : 'Reassign Story'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Story Confirmation Modal */}
      {showDeleteStoryModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteStoryModal(false)}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h2>🗑️ Delete Story?</h2>
            <p className="modal-description">
              Are you sure you want to delete "<strong>{story.title}</strong>"? 
              This will permanently remove the story, all photos, and audio recordings. 
              This action cannot be undone.
            </p>

            <div className="modal-actions">
              <button 
                className="btn-secondary"
                onClick={() => setShowDeleteStoryModal(false)}
                disabled={deletingStory}
              >
                Cancel
              </button>
              <button 
                className="btn-danger"
                onClick={handleDeleteStory}
                disabled={deletingStory}
              >
                {deletingStory ? 'Deleting...' : 'Delete Story'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Photo Confirmation Modal */}
      {showDeletePhotoModal && (
        <div className="modal-overlay" onClick={() => setShowDeletePhotoModal(false)}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h2>🗑️ Delete Photo?</h2>
            <p className="modal-description">
              Are you sure you want to delete this photo? This action cannot be undone.
            </p>

            <div className="modal-actions">
              <button 
                className="btn-secondary"
                onClick={() => setShowDeletePhotoModal(false)}
                disabled={deletingPhoto}
              >
                Cancel
              </button>
              <button 
                className="btn-danger"
                onClick={handleDeletePhoto}
                disabled={deletingPhoto}
              >
                {deletingPhoto ? 'Deleting...' : 'Delete Photo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Comment Confirmation Modal */}
      {showDeleteCommentModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteCommentModal(false)}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h2>🗑️ Delete Comment?</h2>
            <p className="modal-description">
              Are you sure you want to delete this comment? This action cannot be undone.
            </p>

            <div className="modal-actions">
              <button 
                className="btn-secondary"
                onClick={() => { setShowDeleteCommentModal(false); setDeletingCommentId(null); }}
                disabled={deletingComment}
              >
                Cancel
              </button>
              <button 
                className="btn-danger"
                onClick={handleDeleteComment}
                disabled={deletingComment}
              >
                {deletingComment ? 'Deleting...' : 'Delete Comment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Link Story Modal */}
      {showLinkModal && (
        <div className="modal-overlay" onClick={() => setShowLinkModal(false)}>
          <div className="modal-content link-story-modal" onClick={(e) => e.stopPropagation()}>
            <h2>🔗 Link a Story</h2>
            <p className="modal-description">
              Connect this story to another related story in your family circle.
            </p>

            <div className="form-group">
              <label>Search Stories</label>
              <input
                type="text"
                value={linkSearchQuery}
                onChange={(e) => handleSearchStories(e.target.value)}
                placeholder="Search by title..."
                autoFocus
              />
            </div>

            {circleMembers.length > 0 && (
              <div className="form-group">
                <label>Filter by Storyteller</label>
                <select
                  value={linkFilterStoryteller}
                  onChange={(e) => handleLinkFilterStoryteller(e.target.value)}
                >
                  <option value="">All members</option>
                  {circleMembers.map((member) => (
                    <option key={member.userId?._id || member.userId} value={member.userId?._id || member.userId}>
                      {member.userId?.displayName || 'Unknown'}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {searchingStories && (
              <div className="link-search-loading"><div className="spinner"></div> Searching...</div>
            )}

            {linkSearchResults.length > 0 && (
              <div className="link-search-results">
                {linkSearchResults.map((result) => (
                  <div key={result._id} className="link-search-item">
                    <div className="link-search-info">
                      <strong>{result.title}</strong>
                      <span>{result.storytellerId?.displayName} • {new Date(result.createdAt).toLocaleDateString()}</span>
                    </div>
                    <button 
                      className="btn-select-link"
                      onClick={() => handleLinkStory(result._id)}
                      disabled={linkingStory}
                    >
                      {linkingStory ? '...' : 'Link'}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {linkSearchQuery.length >= 2 && !searchingStories && linkSearchResults.length === 0 && (
              <p className="no-search-results">No matching stories found</p>
            )}

            <div className="form-group">
              <label>Relationship</label>
              <select 
                value={selectedLinkRelationship} 
                onChange={(e) => setSelectedLinkRelationship(e.target.value)}
              >
                <option value="related">🔗 Related</option>
                <option value="continuation">➡️ Continuation</option>
                <option value="same_event">📍 Same Event</option>
                <option value="mentioned_in">💬 Mentioned In</option>
                <option value="response_to">↩️ Response To</option>
                <option value="parent_story">👆 Parent Story</option>
                <option value="child_story">👇 Child Story</option>
              </select>
            </div>

            <div className="form-group">
              <label>Note (optional)</label>
              <input
                type="text"
                value={linkNote}
                onChange={(e) => setLinkNote(e.target.value)}
                placeholder="e.g. Dad tells his side of this story"
                maxLength={200}
              />
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowLinkModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Re-enhance Confirmation Modal */}
      {showReenhanceModal && (
        <div className="modal-overlay" onClick={() => setShowReenhanceModal(false)}>
          <div className="modal-content confirm-modal reenhance-modal" onClick={(e) => e.stopPropagation()}>
            <div className="reenhance-icon">✨</div>
            <h2>Re-enhance Story?</h2>
            <p className="modal-description">
              This story has already been AI enhanced. Re-enhancing will regenerate all versions — 
              <strong> Original, Polished, Literary</strong>
              {story.detectedLanguage && story.detectedLanguage !== 'en' && (
                <span> and <strong>English translation</strong></span>
              )}
              — and overwrite the existing ones.
            </p>
            <p className="reenhance-note">
              ✨ Re-enhancements used: <strong>{story.enhancementCount || 1} of 3</strong>
              {(story.enhancementCount || 1) >= 3
                ? ' — limit reached'
                : ` — ${3 - (story.enhancementCount || 1)} remaining after this`}
            </p>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowReenhanceModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn-reenhance-confirm"
                onClick={() => {
                  setShowReenhanceModal(false);
                  handleEnhance();
                }}
              >
                ✨ Yes, Re-enhance
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Time Capsule Modal */}
      {showTimeCapsuleModal && (
        <div className="modal-overlay" onClick={() => setShowTimeCapsuleModal(false)}>
          <div className="modal-content time-capsule-modal" onClick={(e) => e.stopPropagation()}>
            <h2>⏰ Time Capsule</h2>
            <p className="modal-description">
              Lock this story until a future date. Perfect for milestones like birthdays, graduations, or anniversaries.
            </p>

            <div className="form-group">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={timeCapsuleEnabled}
                  onChange={(e) => setTimeCapsuleEnabled(e.target.checked)}
                />
                <span className="toggle-text">Enable Time Capsule</span>
              </label>
            </div>

            {timeCapsuleEnabled && (
              <>
                <div className="form-group">
                  <label>Unlock Date</label>
                  <input
                    type="date"
                    value={unlockDate}
                    onChange={(e) => setUnlockDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div className="form-group">
                  <label>Message (optional)</label>
                  <textarea
                    value={timeCapsuleMessage}
                    onChange={(e) => setTimeCapsuleMessage(e.target.value)}
                    placeholder="A note to whoever opens this time capsule..."
                    rows={3}
                  />
                </div>
              </>
            )}

            <div className="modal-actions">
              <button 
                className="btn-secondary"
                onClick={() => setShowTimeCapsuleModal(false)}
                disabled={savingTimeCapsule}
              >
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={handleSaveTimeCapsule}
                disabled={savingTimeCapsule || (timeCapsuleEnabled && !unlockDate)}
              >
                {savingTimeCapsule ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoryDetail;